import { matchPath } from "react-router-dom";
import { SeoMeta, SeoRule } from "./seo.types";

/* -------------------------------------------------------------------------- */
/* Site constants                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Canonical origin of the deployed app. Overridable per environment so that
 * staging/preview builds never emit canonicals pointing at production.
 * Set REACT_APP_SITE_URL in .env.staging etc.
 */
export const SITE_URL = (
  process.env.REACT_APP_SITE_URL || "https://fleet.codenekt.com"
).replace(/\/+$/, "");

/** Marketing site. This is where organic acquisition actually belongs. */
export const MARKETING_URL = (
  process.env.REACT_APP_MARKETING_URL || "https://www.codenekt.com"
).replace(/\/+$/, "");

export const BRAND = "CodeNekt for Fleet";
export const TITLE_SEPARATOR = " | ";
export const TITLE_MAX_LENGTH = 60;

/** 1200x630 is the safe Open Graph / Twitter summary_large_image size. */
export const DEFAULT_OG_IMAGE = "/og-image.png";

/** i18next language codes this app ships. Used for og:locale only. */
export const SUPPORTED_LOCALES: Record<string, string> = {
  en: "en_US",
  fr: "fr_FR",
  de: "de_DE",
  es: "es_ES",
  it: "it_IT",
  pt: "pt_PT",
};

/* -------------------------------------------------------------------------- */
/* Structured data (login page only)                                          */
/* -------------------------------------------------------------------------- */

const organizationJsonLd: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${MARKETING_URL}/#organization`,
  name: "CodeNekt",
  url: MARKETING_URL,
  logo: {
    "@type": "ImageObject",
    url: `${SITE_URL}/logo512.png`,
    width: 512,
    height: 512,
  },
};

const softwareApplicationJsonLd: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: BRAND,
  applicationCategory: "BusinessApplication",
  applicationSubCategory: "Fleet Management Software",
  operatingSystem: "Web browser",
  url: SITE_URL,
  publisher: { "@id": `${MARKETING_URL}/#organization` },
  featureList: [
    "Vehicle fleet inventory",
    "GPS geolocation and trip history",
    "Maintenance events and scheduling",
    "Claims and incident management",
    "Fuel cards and badges",
    "Total cost of ownership (TCO) analysis",
    "EV battery health monitoring",
    "Driver and collaborator management",
  ],
  inLanguage: Object.keys(SUPPORTED_LOCALES),
};

/* -------------------------------------------------------------------------- */
/* Per-route metadata                                                         */
/* -------------------------------------------------------------------------- */

/**
 * The ONLY indexable page in this application.
 *
 * Every other route is behind PrivateRoute, so a crawler receives the SPA
 * shell and then a redirect to login - i.e. a soft 404. Letting those into
 * the index produces duplicate, contentless results under the brand name and
 * burns crawl budget. They are therefore all `noindex, follow`.
 */
const publicLoginMeta: SeoMeta = {
  title: "Fleet Management Software",
  titleKey: "seo.login.title",
  description:
    "Sign in to CodeNekt for Fleet to manage vehicles, drivers, maintenance, geolocation, claims and total cost of ownership from one dashboard.",
  descriptionKey: "seo.login.description",
  robots: "index, follow",
  canonicalPath: "/",
  ogType: "website",
  jsonLd: [organizationJsonLd, softwareApplicationJsonLd],
};

/** Shown at "/" once a token is present - avoids a "Login" title in-app. */
const authenticatedRootMeta: SeoMeta = {
  title: "Dashboard",
  robots: "noindex, nofollow",
};

/**
 * Order matters: the first match wins, so declare specific patterns before
 * their wildcard parents.
 */
export const SEO_RULES: SeoRule[] = [
  { path: "/", meta: publicLoginMeta },

  /* --- Public signup funnel -------------------------------------------------
     /register is the second genuinely indexable page in this app: it is public,
     has real content, and is a legitimate landing target for branded
     navigational queries ("codenekt sign up"). The two inner steps are not —
     /register/plan requires a session and /register/complete is a transient
     post-payment redirect, so both are noindex and both canonicalise to
     /register to keep the funnel consolidated on one URL. */
  {
    path: "/register",
    meta: {
      title: "Create your fleet account",
      titleKey: "seo.register.title",
      description:
        "Start a 14-day free trial of CodeNekt for Fleet. Manage vehicles, drivers, maintenance, geolocation and total cost of ownership from one dashboard.",
      descriptionKey: "seo.register.description",
      robots: "index, follow",
      canonicalPath: "/register",
      ogType: "website",
    },
  },
  {
    path: "/register/plan",
    meta: { title: "Choose your plan", canonicalPath: "/register" },
  },
  {
    path: "/register/complete",
    meta: { title: "Activating your subscription", canonicalPath: "/register" },
  },

  /* --- Documents contractuels ------------------------------------------------
     Indexables, volontairement. Ce sont deux pages publiques a contenu reel,
     stable, et c'est la reponse a une requete navigationnelle legitime
     ("codenekt terms of service"). Elles renforcent aussi la confiance sur le
     tunnel d'inscription, qu'elles servent directement.

     Pas de canonicalPath vers /register : ce sont des destinations a part
     entiere, pas des etapes du tunnel. Les rabattre sur /register ferait
     disparaitre des resultats les seules pages qui repondent a la question
     posee. */
  {
    path: "/legal/terms",
    meta: {
      title: "Terms of Service",
      description:
        "The terms governing your use of CodeNekt for Fleet, published by CodeNekt For Fleet OÜ.",
      robots: "index, follow",
      canonicalPath: "/legal/terms",
      ogType: "website",
    },
  },
  {
    path: "/legal/dpa",
    meta: {
      title: "Data Processing Agreement",
      description:
        "The GDPR Article 28 data processing agreement between CodeNekt For Fleet OÜ and its customers.",
      robots: "index, follow",
      canonicalPath: "/legal/dpa",
      ogType: "website",
    },
  },

  /* --- Public utility pages: no index, but crawlable so the noindex is seen.
         These carry one-time tokens in the query string. --------------------- */
  {
    path: "/invitation",
    meta: { title: "Accept your invitation", sensitive: true },
  },
  {
    path: "/generate_password",
    meta: { title: "Accept your invitation", sensitive: true },
  },
  {
    path: "/enter_email_forgot_password",
    meta: { title: "Reset your password" },
  },
  {
    path: "/enter_code_forgot_password",
    meta: { title: "Enter your reset code", sensitive: true },
  },
  { path: "/forgot_password", meta: { title: "Choose a new password", sensitive: true } },
  { path: "/employee", meta: { title: "Redirecting" } },

  /* --- Application shell: noindex, follow ------------------------------------ */
  { path: "/dashboard", meta: { title: "Dashboard" } },
  { path: "/billing", meta: { title: "Billing" } },
  { path: "/ai-agent", meta: { title: "AI agent" } },
  { path: "/aide", meta: { title: "Help centre" } },
  { path: "/carteAndBadge", meta: { title: "Cards and badges" } },
  { path: "/geolocalisation", meta: { title: "Geolocation" } },

  { path: "/vehicule/create", meta: { title: "Add a vehicle" } },
  { path: "/vehicule/update/:id/create-event", meta: { title: "New vehicle event" } },
  { path: "/vehicule/update/:id/event/:eventId", meta: { title: "Vehicle event" } },
  { path: "/vehicule/update/:id", meta: { title: "Edit vehicle" } },
  { path: "/vehicule/:id/history", meta: { title: "Vehicle history" } },
  { path: "/vehicule/battery-health/:id", meta: { title: "Battery health" } },
  { path: "/vehicule/list", meta: { title: "Vehicles" } },

  { path: "/collaborator/create", meta: { title: "Add a collaborator" } },
  { path: "/collaborator/update/:id", meta: { title: "Edit collaborator" } },
  { path: "/collaborator/list", meta: { title: "Collaborators" } },

  { path: "/notification/list", meta: { title: "Notifications" } },

  { path: "/claims/create", meta: { title: "Declare a claim" } },
  { path: "/claims/update/:id", meta: { title: "Edit claim" } },
  { path: "/claims/list", meta: { title: "Claims" } },

  { path: "/documents/list", meta: { title: "Documents" } },

  { path: "/tco", meta: { title: "Total cost of ownership" } },
  /* Analyse & optimisation. Both are sidebar destinations; without an entry
     here they fell through to FALLBACK_META and the tab read just the brand
     name, which is also what the browser stores in history and what a shared
     link previews as. */
  { path: "/fiscalite", meta: { title: "Tax optimisation" } },
  { path: "/analyses", meta: { title: "Advanced analytics" } },
  { path: "/parametre", meta: { title: "Settings" } },
];

/** Fallback for any route not declared above (including 404s). */
export const FALLBACK_META: SeoMeta = {
  title: BRAND,
  robots: "noindex, nofollow",
};

/* -------------------------------------------------------------------------- */
/* Resolution                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Resolve the metadata for a pathname.
 *
 * `isAuthenticated` only affects "/", which renders Login for guests and the
 * Dashboard for signed-in users. Crawlers are always unauthenticated, so the
 * indexable variant is what search engines see.
 */
export const resolveSeo = (
  pathname: string,
  isAuthenticated = false
): SeoMeta => {
  if (pathname === "/" || pathname === "") {
    return isAuthenticated ? authenticatedRootMeta : publicLoginMeta;
  }

  const rule = SEO_RULES.find((candidate) =>
    matchPath({ path: candidate.path, end: candidate.end ?? true }, pathname)
  );

  return rule ? rule.meta : FALLBACK_META;
};

/** Build the final document title, brand-suffixed and length-guarded. */
export const buildTitle = (title: string): string => {
  const full = title === BRAND ? BRAND : `${title}${TITLE_SEPARATOR}${BRAND}`;
  if (full.length <= TITLE_MAX_LENGTH) return full;
  // Prefer keeping the brand: truncate the leading segment instead.
  const room = TITLE_MAX_LENGTH - BRAND.length - TITLE_SEPARATOR.length - 1;
  return room > 8
    ? `${title.slice(0, room).trimEnd()}…${TITLE_SEPARATOR}${BRAND}`
    : full;
};
